import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AppLayout from '../layout/AppLayout';
import { rateService } from '../../services/api';
import { INK, MUTED, RULE, formatINR } from '../analytics/analyticsUi';

const CATEGORY_INFO = {
  MARKETING: { label: 'Marketing', text: 'Promotions, offers and announcements' },
  MARKETING_LITE: { label: 'Marketing (lite)', text: 'Marketing sent through the Marketing Messages API' },
  UTILITY: { label: 'Utility', text: 'Updates about an order, booking or account' },
  AUTHENTICATION: { label: 'Authentication', text: 'One-time passcodes' },
  AUTHENTICATION_INTERNATIONAL: { label: 'Authentication (international)', text: 'Passcodes in countries with international rates' },
  SERVICE: { label: 'Service', text: 'Replies to customers, when charged' },
  REFERRAL_CONVERSION: { label: 'Referral conversion', text: 'Conversations started from ads' }
};

const COMMON_COUNTRIES = ['IN', 'AE', 'SA', 'QA', 'OM', 'KW', 'BH', 'US', 'GB', 'SG', 'MY', 'ID', 'LK', 'NP', 'BD', 'AU', 'CA', 'DE', 'FR'];

const countryName = (code) => {
  if (code === 'DEFAULT') return 'Default';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch (err) {
    return code;
  }
};

const toInput = (value) => (value === null || value === undefined ? '' : String(value));

const RateInput = ({ value, onChange, label, placeholder }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{ min: 0, step: 0.0001, 'aria-label': label, style: { textAlign: 'right' } }}
    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
    sx={{ width: 150 }}
  />
);

const MessageRates = () => {
  const [rates, setRates] = useState([]);
  const [categories, setCategories] = useState(Object.keys(CATEGORY_INFO));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [country, setCountry] = useState('DEFAULT');
  const [extraCountries, setExtraCountries] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [addValue, setAddValue] = useState(null);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await rateService.getRates();
      setRates(response.data.rates || []);
      if (response.data.categories?.length) setCategories(response.data.categories);
    } catch (err) {
      setLoadError('Message rates could not be loaded. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savedCountries = useMemo(
    () => [...new Set(rates.map((r) => r.country))].filter((c) => c !== 'DEFAULT').sort(),
    [rates]
  );
  const countries = useMemo(
    () => ['DEFAULT', ...[...new Set([...savedCountries, ...extraCountries])].sort()],
    [savedCountries, extraCountries]
  );

  const savedFor = useCallback((code) => {
    const rows = {};
    categories.forEach((category) => {
      const row = rates.find((r) => r.country === code && r.category === category);
      rows[category] = { metaRate: toInput(row?.metaRate), resaleRate: toInput(row?.resaleRate) };
    });
    return rows;
  }, [rates, categories]);

  // Reset the editable copy when switching country or after saving
  useEffect(() => { setDraft(savedFor(country)); }, [country, savedFor]);

  const defaults = savedFor('DEFAULT');
  const saved = savedFor(country);
  const dirty = categories.some((c) =>
    (draft[c]?.metaRate ?? '') !== saved[c].metaRate || (draft[c]?.resaleRate ?? '') !== saved[c].resaleRate
  );

  const setRate = (category, field, value) =>
    setDraft((prev) => ({ ...prev, [category]: { ...prev[category], [field]: value } }));

  const save = async () => {
    const payload = categories.map((category) => ({
      category,
      metaRate: draft[category]?.metaRate ?? '',
      resaleRate: draft[category]?.resaleRate ?? ''
    }));
    if (payload.some((r) => [r.metaRate, r.resaleRate].some((v) => v !== '' && (Number.isNaN(Number(v)) || Number(v) < 0)))) {
      setError('Rates must be 0 or more.');
      return;
    }
    try {
      setSaving(true);
      const response = await rateService.saveCountryRates(country, payload);
      setRates(response.data.rates || []);
      setExtraCountries((prev) => prev.filter((c) => c !== country));
      setSuccess(`${countryName(country)} rates saved`);
    } catch (err) {
      setError(err.response?.data?.error || 'The rates could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const removeCountry = async () => {
    try {
      setRemoving(true);
      await rateService.deleteCountryRates(country);
      setRates((prev) => prev.filter((r) => r.country !== country));
      setExtraCountries((prev) => prev.filter((c) => c !== country));
      setSuccess(`${countryName(country)} rates removed. The default rates apply there now.`);
      setCountry('DEFAULT');
      setRemoveOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'The rates could not be removed. Try again.');
    } finally {
      setRemoving(false);
    }
  };

  const addCountry = (code) => {
    if (!code) return;
    if (!countries.includes(code)) setExtraCountries((prev) => [...prev, code]);
    setCountry(code);
    setAddValue(null);
  };

  const countryOptions = useMemo(() => {
    const all = [...COMMON_COUNTRIES];
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach((a) => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach((b) => {
      const code = `${a}${b}`;
      if (!all.includes(code) && countryName(code) !== code) all.push(code);
    }));
    return all.filter((code) => !countries.includes(code));
  }, [countries]);

  const isDefault = country === 'DEFAULT';

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
              Message rates
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, maxWidth: '75ch' }}>
              Meta doesn't share cost for accounts billed through your credit line, so the portal estimates it from these rates:
              INR per delivered paid message. Customers see cost at your rate; only admins see Meta's rate and your margin.
            </Typography>
          </Box>
          <Button
            component="a"
            href="https://developers.facebook.com/docs/whatsapp/pricing"
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            Meta's current rates
          </Button>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
            {loadError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={country}
            onChange={(e, value) => value && setCountry(value)}
            aria-label="Country"
            sx={{ flexWrap: 'wrap' }}
          >
            {countries.map((code) => (
              <ToggleButton key={code} value={code} sx={{ textTransform: 'none', px: 1.75 }}>
                {code === 'DEFAULT' ? 'Default' : `${countryName(code)} (${code})`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Autocomplete
            size="small"
            sx={{ width: 240 }}
            options={countryOptions}
            value={addValue}
            onChange={(e, code) => addCountry(code)}
            getOptionLabel={(code) => `${countryName(code)} (${code})`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add a country"
                InputProps={{ ...params.InputProps, startAdornment: <AddIcon fontSize="small" sx={{ color: MUTED, ml: 0.5 }} /> }}
              />
            )}
          />
        </Box>

        <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5 }}>
            <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK }}>
              {isDefault ? 'Default rates' : `${countryName(country)} rates`}
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
              {isDefault
                ? 'Used for every country that has no rates of its own.'
                : 'Leave a field empty to use the default rate for that category.'}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ px: 3, pb: 3 }}>
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={48} />)}
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                    <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>Category</TableCell>
                    <TableCell align="right">Meta's rate</TableCell>
                    <TableCell align="right">Your rate</TableCell>
                    <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 } }}>Margin per message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => {
                    const info = CATEGORY_INFO[category] || { label: category, text: '' };
                    const row = draft[category] || { metaRate: '', resaleRate: '' };
                    // Empty country fields fall back to the default rate
                    const effective = (value, fallback) => {
                      if (value !== '') return Number(value);
                      if (!isDefault && fallback !== '' && fallback !== undefined) return Number(fallback);
                      return null;
                    };
                    const meta = effective(row.metaRate, defaults[category]?.metaRate);
                    const resale = effective(row.resaleRate, defaults[category]?.resaleRate);
                    const hasBoth = meta !== null && resale !== null && Number.isFinite(meta) && Number.isFinite(resale);
                    const margin = hasBoth ? resale - meta : null;
                    return (
                      <TableRow key={category} sx={{ '& td': { borderColor: RULE, py: 1.25 } }}>
                        <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>
                          <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{info.label}</Typography>
                          <Typography variant="caption" sx={{ color: MUTED }}>{info.text}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <RateInput
                            label={`${info.label} Meta rate`}
                            value={row.metaRate}
                            placeholder={!isDefault ? defaults[category]?.metaRate || '' : ''}
                            onChange={(v) => setRate(category, 'metaRate', v)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <RateInput
                            label={`${info.label} your rate`}
                            value={row.resaleRate}
                            placeholder={!isDefault ? defaults[category]?.resaleRate || '' : ''}
                            onChange={(v) => setRate(category, 'resaleRate', v)}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 }, fontVariantNumeric: 'tabular-nums' }}>
                          {margin === null ? (
                            <Typography variant="body2" sx={{ color: MUTED }}>–</Typography>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ color: margin < 0 ? '#C2410C' : INK, fontWeight: 500 }}>
                                {formatINR(margin)}
                              </Typography>
                              {meta > 0 && (
                                <Typography variant="caption" sx={{ color: MUTED }}>
                                  {Math.round((margin / meta) * 100)}%
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 3 }, py: 2, borderTop: `1px solid ${RULE}` }}>
            <Box>
              {!isDefault && (
                <Button color="error" onClick={() => setRemoveOpen(true)} disabled={saving} sx={{ textTransform: 'none' }}>
                  Remove {countryName(country)} rates
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {dirty && (
                <Button onClick={() => setDraft(savedFor(country))} disabled={saving} sx={{ textTransform: 'none' }}>
                  Discard changes
                </Button>
              )}
              <Button
                variant="contained"
                onClick={save}
                disabled={!dirty || saving || loading}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {saving ? 'Saving…' : 'Save rates'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Typography variant="body2" sx={{ color: MUTED, mt: 2, maxWidth: '75ch' }}>
          Free messages (replies within the customer service window and conversations started from ads) are never charged.
          Estimates don't include Meta's volume tier discounts. Check the amounts on your Meta invoice.{' '}
          <Link href="https://business.facebook.com/billing_hub" target="_blank" rel="noopener noreferrer">Open billing in Meta Business Suite</Link>
        </Typography>

        <Dialog open={removeOpen} onClose={() => !removing && setRemoveOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Remove {countryName(country)} rates?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Messages to {countryName(country)} will be estimated with the default rates.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRemoveOpen(false)} disabled={removing} sx={{ textTransform: 'none' }}>Keep rates</Button>
            <Button
              variant="contained"
              color="error"
              onClick={removeCountry}
              disabled={removing}
              startIcon={removing ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {removing ? 'Removing…' : 'Remove rates'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default MessageRates;
